"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
  referralCode: string | null;
  onActivate: (code: string) => Promise<void>;
  isLoading: boolean;
}

export const ReferralModal: React.FC<ReferralModalProps> = ({
  isOpen,
  onClose,
  referralCode: initialReferralCode,
  onActivate,
  isLoading,
}) => {
  const [value, setValue] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && initialReferralCode) {
      setValue(initialReferralCode);
    }
  }, [isOpen, initialReferralCode]);

  const handleClose = () => {
    if (!isSubmitting) {
      setValue("");
      onClose();
    }
  };

  const handleActivate = async () => {
    if (value && value.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onActivate(value.trim());
        handleClose();
      } catch (error) {
        // Error is handled in the parent component
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && value && value.trim() && !isSubmitting) {
      handleActivate();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }} // ease-out-quad
            className="fixed top-1/2 left-1/2 w-full max-w-md bg-[#0E0D0B] border border-[#262523] rounded-lg z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#262523]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#EA5555] rounded-lg flex items-center justify-center">
                  <Flame className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-medium tracking-[-1.5px] text-white uppercase tracking-wider">
                  ACTIVATE REFERRAL
                </h2>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="p-2 hover:bg-[#262523] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Description */}
              <p className="text-sm text-[#9CA3AF] mb-6 uppercase tracking-wider">
                Enter your referral code to unlock rewards and start earning points!
              </p>

              {/* Input Field */}
              <div className="mb-6">
                <label htmlFor="referral-code" className="block text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">
                  Referral Code
                </label>
                <input
                  id="referral-code"
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter referral code"
                  disabled={isSubmitting}
                  className={cn(
                    "w-full px-4 py-3 bg-[#1A1917] border border-[#262523] rounded-lg",
                    "text-white placeholder-[#6B7280] outline-none",
                    "focus:border-[#EA5555] focus:bg-[#1A1917]/80",
                    "transition-all duration-200",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                  autoFocus
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 w-full">
                <Button
                  variant="monochrome"
                  onClick={handleClose}
                  className="w-full bg-[#262523] hover:bg-[#3A3A3A] text-white"
                  disabled={isSubmitting}
                >
                  CANCEL
                </Button>
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={handleActivate}
                  disabled={!value || !value.trim() || isSubmitting || isLoading}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-spicenet-black"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      ACTIVATING...
                    </span>
                  ) : (
                    "ACTIVATE"
                  )}
                </Button>
              </div>

              {/* Info Text */}
              <div className="mt-4 text-center">
                <p className="text-xs text-[#6B7280] uppercase tracking-wider">
                  Tip: Share your code to earn rewards
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};